import * as ko from "knockout";
import {ObjectTypeId} from "../../../constants";
import {memoryStore} from "../../../util/memorystore";
import {getAnyValue, getObjectDefinition, IObjectDefinition, isNullOrUndefined} from "../../../util/misc";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {global, lm} from "../../../appContext";
import {TBaseOrObservableType} from "../../../index";
import {IDialogOpt} from "../../dialog/manager";
import {nsFa} from "../../fa/fn";
import i = nsFa.i;
import builderHtml from './builder.html?raw';

export let template = builderHtml;

export interface IExpressionBuilderParams {
    objectTypeId?: TBaseOrObservableType<ObjectTypeId>
    objectTypeIds: ObjectTypeId[]
    expression?: TBaseOrObservableType<string>
    canEditInDialog?: boolean // whether to show button to edit the expression in a (new) dialog
    /** Set by dialogManager.addCustom */
    __dialog?: IDialogOpt
    /** Set by dialogManager.addComponent */
    dialog?: { doClose?: () => void; title?: TBaseOrObservableType<string>; id?: string }
    save?: (expression: string) => void
    /** Called when the dialog closes without Save (Cancel / click-away). */
    cancel?: () => void
}

export interface IPreviewParams {
    objectid: KnockoutObservable<number | null>
    objectTypeId: KnockoutObservable<ObjectTypeId | null>
    expression: KnockoutObservable<string>
    fetchEvaluation: { fn: (() => void) | null }
}

export interface IExpressionParams {
    canOpenInDialog: boolean
    expression: KnockoutObservable<string>
    objectTypeId: KnockoutObservable<ObjectTypeId | null>
    objectTypeIds: KnockoutObservableArray<ObjectTypeId>
    objectid: KnockoutObservable<number | null>
    submit: () => void
    save?: ((expression: string) => void) | undefined
    cancel: () => void
}

export interface IContextParams {
    objectTypeId: KnockoutObservable<ObjectTypeId | null>
    objectid: KnockoutObservable<number | null>
}

export class viewModel {
    private readonly objectid: KnockoutObservable<number | null> = ko.observable(null); // selected user/group/department id
    private readonly availableObjectTypes: IObjectDefinition[] = [];
    private readonly expression: KnockoutObservable<string> = ko.observable('');
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly objectTypeId: KnockoutObservable<ObjectTypeId | null> = ko.observable(null);
    private readonly selectedSecondaryObjectTypeIds: KnockoutObservableArray<ObjectTypeId> = ko.observableArray();
    private readonly commitExpression: ((expression: string) => void) | undefined;
    private readonly hasWriteBack: boolean;
    private initialExpression = '';
    private didSave = false;
    private allowClose = false;
    private discardConfirmOpen = false;
    private forceCloseFn: (() => void) | undefined;
    private expressionParams: IExpressionParams
    private contextParams: IContextParams
    private previewParams: IPreviewParams


    constructor(private params: IExpressionBuilderParams) {
        if (ko.isObservable(params.__dialog?.width)) {
            params.__dialog.width('auto')
        }
        if (!getAnyValue(params.objectTypeId)) {
            const availableObjectIds = [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group];
            const org = global.login.selectedOrganization();
            const iseHubSupported = !!org?.ehub;
            if (iseHubSupported) {
                availableObjectIds.push(ObjectTypeId.FormData, ObjectTypeId.ExternalContact);
            }
            this.availableObjectTypes.push(...availableObjectIds.map(getObjectDefinition).filter((d): d is IObjectDefinition => d != null));
        }
        this.objectTypeId(getAnyValue(params.objectTypeId) ?? ObjectTypeId.User);
        this.kosub.subscribe(this.objectid, id => {
            memoryStore.get('test.object.id.' + ko.unwrap(this.objectTypeId))(id)
        })
        this.selectedSecondaryObjectTypeIds(this.params.objectTypeIds);

        if (!isNullOrUndefined(params.expression)) {
            if (ko.isObservable(params.expression)) {
                this.expression(ko.unwrap(params.expression) ?? '');
            } else {
                this.expression(String(params.expression ?? ''));
            }
        }
        this.initialExpression = this.expression();
        this.expression.extend({throttle: 500})
        this.kosub.subscribe(this.objectTypeId, this.init.bind(this));

        // Explicit save only — or write back to the source observable (sync templates / rulesets / message flows).
        this.commitExpression = params.save ?? (
            ko.isObservable(params.expression)
                ? (expr) => (params.expression as KnockoutObservable<string>)(expr)
                : undefined
        );
        this.hasWriteBack = !!this.commitExpression;

        this.expressionParams = {
            canOpenInDialog: false,
            expression: this.expression,
            objectTypeId: this.objectTypeId,
            objectTypeIds: this.selectedSecondaryObjectTypeIds,
            objectid: this.objectid,
            submit: this.submit.bind(this),
            save: this.hasWriteBack ? this.save.bind(this) : undefined,
            cancel: this.hasWriteBack ? this.requestClose.bind(this) : this.closeDialog.bind(this),
        }
        this.contextParams = {
            objectTypeId: this.objectTypeId,
            objectid: this.objectid,
        }
        this.previewParams = {
            objectTypeId: this.objectTypeId,
            objectid: this.objectid,
            expression: this.expression,
            fetchEvaluation: {fn: null},
        }

        this.init();
        if (this.hasWriteBack) {
            this.installCloseGuard();
        }
    }

    private init() {
        this.isReady(false);
        this.objectid(memoryStore.get('test.object.id.' + ko.unwrap(this.objectTypeId))());
        this.isReady(true);
    }

    private isChanged() {
        return this.expression() !== this.initialExpression;
    }

    private getDialogElementId(): string | undefined {
        return this.params.__dialog?.id ?? this.params.dialog?.id;
    }

    private installCloseGuard() {
        if (this.params.dialog?.doClose) {
            this.forceCloseFn = this.params.dialog.doClose;
            this.params.dialog.doClose = () => this.requestClose();
        }
        if (this.params.__dialog?.close) {
            this.forceCloseFn = this.params.__dialog.close;
            this.params.__dialog.close = () => this.requestClose();
        }

        // Static backdrop + Escape off: outside-click / Esc go through requestClose (not Bootstrap hide).
        // Also intercept hide.bs.modal so X (data-dismiss) cannot bypass the unsaved-changes confirm.
        // (Bootstrap 3.4 supports preventDefault on hide without the BS4 transitioning lock.)
        requestAnimationFrame(() => {
            const id = this.getDialogElementId();
            if (!id) {
                return;
            }
            const $modal = jQuery('#' + id);
            const modal = $modal.data('bs.modal') as { options?: { backdrop?: boolean | string; keyboard?: boolean } } | undefined;
            if (modal?.options) {
                modal.options.backdrop = 'static';
                modal.options.keyboard = false;
            }

            $modal.on('hide.bs.modal.expressionBuilder', (e) => {
                if (this.didSave || this.allowClose || !this.isChanged()) {
                    return;
                }
                e.preventDefault();
                this.requestClose();
            });

            $modal.on('click.expressionBuilder', (e) => {
                if (e.target === e.currentTarget) {
                    this.requestClose();
                }
            });

            $modal.on('keydown.expressionBuilder', (e) => {
                if (e.key === 'Escape' || (e as JQuery.KeyDownEvent).which === 27) {
                    this.requestClose();
                }
            });
        });
    }

    private showDiscardChangesConfirm(onConfirm: () => void) {
        if (this.discardConfirmOpen) {
            return;
        }
        this.discardConfirmOpen = true;
        // Static backdrop + no X: must choose Yes/No so discardConfirmOpen always resets.
        global.dialogManager.addConfirm({
            isClosable: ko.observable(false),
            buttons: {
                confirm: {
                    label: `yes, continue`,
                    onClick: () => {
                        this.discardConfirmOpen = false;
                        onConfirm();
                    },
                },
                cancel: {
                    label: `no, cancel`,
                    onClick: () => {
                        this.discardConfirmOpen = false;
                    },
                },
            },
            content: `
            <p>${lm.t('you are about to discard all changes')}</p>
            <p>${lm.t('do you wish to continue')}</p>
            `,
            title: `${i('fa-undo')} ((discard changes))`,
        });
    }

    private requestClose() {
        if (this.discardConfirmOpen) {
            return;
        }
        if (this.didSave || this.allowClose || !this.isChanged()) {
            this.forceClose();
            return;
        }
        this.showDiscardChangesConfirm(() => {
            this.allowClose = true;
            this.forceClose();
        });
    }

    private closeDialog() {
        this.params.__dialog?.close?.();
        this.params.dialog?.doClose?.();
    }

    private forceClose() {
        (this.forceCloseFn ?? this.closeDialog.bind(this))();
    }

    private submit() {
        this.previewParams.fetchEvaluation.fn?.()
    }

    private save() {
        this.didSave = true;
        this.allowClose = true;
        this.commitExpression?.(this.expression());
        this.forceClose();
    }

    private getObjectType(): IObjectDefinition | null {
        const typeId = this.objectTypeId();
        if (!typeId) {
            return null
        }
        return getObjectDefinition(typeId)
    }

    private getObjDef = getObjectDefinition;

    dispose() {
        const id = this.getDialogElementId();
        if (id) {
            jQuery('#' + id).off('.expressionBuilder');
        }
        if (!this.didSave) {
            this.params.cancel?.();
        }
        this.kosub.unsuscribeAll();
    }
}
